<template>
  <LcTabs v-bind="{ tabs }">
    <template v-if="hasOptionalAddOn" #header>
      <div class="mx-100">
        <h2 v-if="isPickLottery" class="mt-200 mb-100 headline-h5-headline">
          {{ $t('results.tiertablepicklottery.headline') }}
        </h2>

        <ResultsAddOnToggle />
      </div>
    </template>

    <template #tab-panel="{ tab }">
      <div class="tab-panel-container mx-100">
        <ResultsTierTableCombined
          v-if="hasCombinedTierTable"
          v-bind="{
            addOns,
            licensedTerritory,
            lotteryId,
            prizeCategories,
            prizeTierCategories,
            type: tab.name as TierTableType,
          }"
        />
        <TierTable
          v-else
          v-bind="{
            addOns,
            licensedTerritory,
            lotteryId,
            groupedPrizeTiers,
            type: tab.name as TierTableType,
          }"
        />
      </div>
    </template>
  </LcTabs>
</template>

<script lang="ts" setup>
import type { TierTablePrizeTier, TierTableType } from '#product/app/components/TierTable.vue'

type SimplePrizeCategory = {
  group: string
  name: string
}

const { getTranslation, t } = useI18nUtils()
const {
  addOns,
  hasCombinedTierTable,
  hasOptionalAddOn,
  isWinnersTabVisible,
  licensedTerritory,
  lotteryId,
  prizeCategories,
  prizeTierCategories,
  showWithAddOn,
} = await useResultDetails()

const { isPickLottery } = useLotteryType(lotteryId)

const { activeTabs: tabs } = useTabs(() => [
  {
    name: 'prizes',
    title: t('results.tiertable.tab.prizes', 2),
  },
  {
    active: isWinnersTabVisible,
    name: 'winners',
    title: t('results.tiertable.tab.winners'),
  },
])

const currentPrizeCategories = computed(() => prizeCategories.value.filter(getCurrentCategory))

const groupedPrizeTiers = computed(() =>
  prizeTierCategories.value.filter(getCurrentTier).map(({ group, prizeTiers }, categoryIndex) => ({
    headline: getTranslation({ key: `results.tiertable.headline.${group}` }),
    prizeTiers: prizeTiers.map<TierTablePrizeTier>((prizeTier, prizeTierIndex) => {
      const { prize, winners } = currentPrizeCategories.value[categoryIndex].prizes[prizeTierIndex]

      return {
        ...prizeTier,
        prize,
        winners,
      }
    }),
  })),
)

const getCurrentCategory = ({ group, name }: SimplePrizeCategory) => {
  const groupMembers = prizeCategories.value.filter((category) => category.group === group)

  if (groupMembers.length > 1 && group === 'DEFAULT' && showWithAddOn.value) {
    return name !== 'DEFAULT'
  }

  return name === 'DEFAULT'
}
const getCurrentTier = ({ group, name }: SimplePrizeCategory) =>
  !!currentPrizeCategories.value.find(
    (category) => category.name === name && category.group === group,
  )
</script>

<style lang="scss" scoped>
.tab-panel-container {
  border-top: separator('line');
}
</style>
